import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../App';
import { 
    Box, Typography, Button, TextField, Paper, Alert,
    Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
    IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
    Stack, Chip, Container, Collapse, Tabs, Tab, Grid, Card, CardContent,
    Select, MenuItem, FormControl, InputLabel, Switch, FormControlLabel
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { 
    FaPlus, FaTrash, FaChevronDown, FaChevronUp, FaSave, 
    FaTrophy, FaCalendarAlt, FaHandshake, FaQrcode, FaInfoCircle, 
    FaPlay, FaCheck, FaTimes, FaUtensils, FaEdit
} from 'react-icons/fa';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import ConfirmModal from '../../components/ConfirmModal';

const AdminGruempelTurnier = () => {
    const [turniere, setTurniere] = useState([]);
    const [open, setOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // Form State for creation
    const [name, setName] = useState('');
    const [date, setDate] = useState(dayjs());
    const [location, setLocation] = useState('');
    const [description, setDescription] = useState('');
    const [contact, setContact] = useState('Turnierleitung');

    // Delete State
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [turnierToDelete, setTurnierToDelete] = useState(null);
    
    // Expandable Row State
    const [expandedRow, setExpandedRow] = useState(null);

    const fetchTurniere = () => {
        axios.get(`${API_BASE_URL}/api/gruemp`, { withCredentials: true })
            .then(res => {
                setTurniere(res.data);
            })
            .catch(err => {
                console.error(err);
                toast.error("Fehler beim Laden der Turniere.");
            });
    };

    useEffect(() => {
        fetchTurniere();
    }, []);

    const handleCreate = () => {
        setIsSubmitting(true);
        const data = {
            name: name || 'Grümpelturnier',
            date: date.toDate(),
            location,
            description,
            contact
        };

        axios.post(`${API_BASE_URL}/api/gruemp`, data, { withCredentials: true })
            .then(() => {
                toast.success("Grümpelturnier erfolgreich angelegt!");
                setOpen(false);
                fetchTurniere();
                // Reset form
                setName('');
                setDate(dayjs());
                setLocation('');
                setDescription('');
                setContact('Turnierleitung');
            })
            .catch(err => {
                toast.error("Fehler beim Anlegen: " + (err.response?.data?.message || err.message));
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    const handleDeleteClick = (id) => {
        setTurnierToDelete(id);
        setConfirmDeleteOpen(true);
    };

    const confirmDelete = () => {
        if (!turnierToDelete) return;
        
        axios.delete(`${API_BASE_URL}/api/gruemp/${turnierToDelete}`, { withCredentials: true })
            .then(() => {
                toast.success("Turnier gelöscht");
                fetchTurniere();
                setConfirmDeleteOpen(false);
                setTurnierToDelete(null);
            })
            .catch(err => {
                toast.error("Fehler beim Löschen: " + (err.response?.data?.message || err.message));
            });
    };

    return (
        <Container maxWidth={false} disableGutters>
            <Paper sx={{ p: { xs: 2, md: 3 }, mb: 4 }}>
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', md: 'row' },
                    justifyContent: 'space-between', 
                    alignItems: { xs: 'flex-start', md: 'center' },
                    gap: 2,
                    mb: 3
                }}>
                    <Typography variant="h5" component="h1" color="primary" fontWeight="bold">
                        Grümpelturnier-Verwaltung
                    </Typography>
                    <Button variant="contained" startIcon={<FaPlus />} onClick={() => setOpen(true)}>
                        Neues Grümpelturnier
                    </Button>
                </Box>

                {turniere.length === 0 ? (
                    <Alert severity="info">Noch kein Grümpelturnier angelegt. Klicken Sie oben auf "Neues Grümpelturnier" um eines zu erstellen.</Alert>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold', width: 50 }}></TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Datum</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Austragungsort</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Aktionen</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {turniere.map(t => (
                                    <React.Fragment key={t._id}>
                                        <TableRow hover>
                                            <TableCell>
                                                <IconButton size="small" onClick={() => setExpandedRow(expandedRow === t._id ? null : t._id)}>
                                                    {expandedRow === t._id ? <FaChevronUp /> : <FaChevronDown />}
                                                </IconButton>
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>{t.name}</TableCell>
                                            <TableCell>{dayjs(t.date).format('DD.MM.YYYY')}</TableCell>
                                            <TableCell>{t.location || 'Keine Angabe'}</TableCell>
                                            <TableCell>
                                                {t.isPublished ? (
                                                    <Chip label="Öffentlich" size="small" color="success" />
                                                ) : (
                                                    <Chip label="Entwurf" size="small" color="warning" />
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <IconButton color="error" onClick={() => handleDeleteClick(t._id)} size="small" title="Löschen">
                                                    <FaTrash />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                                                <Collapse in={expandedRow === t._id} timeout="auto" unmountOnExit>
                                                    <Box sx={{ margin: 2, mb: 4 }}>
                                                        <GruempelTurnierManager turnier={t} onUpdate={fetchTurniere} />
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>

            <ConfirmModal 
                open={confirmDeleteOpen}
                onClose={() => setConfirmDeleteOpen(false)}
                onConfirm={confirmDelete}
                title="Grümpelturnier löschen"
                message="Möchten Sie dieses Grümpelturnier wirklich unwiderruflich löschen? Alle Gruppen, Teams und Spielpläne gehen verloren."
                confirmText="Löschen"
                type="delete"
            />

            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Neues Grümpelturnier anlegen</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} mt={1}>
                        <TextField 
                            label="Name des Turniers" 
                            fullWidth 
                            value={name} 
                            onChange={(e) => setName(e.target.value)} 
                            placeholder="Z.B. 34. TCO Grümpelturnier"
                        />
                        <DatePicker 
                            label="Datum"
                            value={date}
                            onChange={(newValue) => setDate(newValue)}
                            slotProps={{ textField: { fullWidth: true } }}
                            format="DD.MM.YYYY"
                        />
                        <TextField 
                            label="Austragungsort" 
                            fullWidth 
                            value={location} 
                            onChange={(e) => setLocation(e.target.value)} 
                            placeholder="Z.B. TCO Tennisplatz Opfingen"
                        />
                        <TextField 
                            label="Beschreibung (optional)" 
                            fullWidth 
                            multiline 
                            rows={3}
                            value={description} 
                            onChange={(e) => setDescription(e.target.value)} 
                        />
                        <TextField 
                            label="Ansprechpartner" 
                            fullWidth 
                            value={contact} 
                            onChange={(e) => setContact(e.target.value)} 
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Abbrechen</Button>
                    <Button onClick={handleCreate} variant="contained" disabled={isSubmitting}>
                        Anlegen
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

// ============================================================================
// GruempelTurnierManager component
// ============================================================================
const GruempelTurnierManager = ({ turnier, onUpdate }) => {
    const [activeTab, setActiveTab] = useState(0);
    const [isSaving, setIsSaving] = useState(false);

    // Global Confirm State for Manager actions
    const [managerConfirmOpen, setManagerConfirmOpen] = useState(false);
    const [managerConfirmTitle, setManagerConfirmTitle] = useState('');
    const [managerConfirmMessage, setManagerConfirmMessage] = useState('');
    const [onConfirmAction, setOnConfirmAction] = useState(null);

    const triggerConfirm = (title, message, action) => {
        setManagerConfirmTitle(title);
        setManagerConfirmMessage(message);
        setOnConfirmAction(() => action);
        setManagerConfirmOpen(true);
    };

    const handleConfirmManagerAction = () => {
        if (onConfirmAction) {
            onConfirmAction();
        }
        setManagerConfirmOpen(false);
    };

    // Tab 0: Details State
    const [name, setName] = useState(turnier.name || '');
    const [date, setDate] = useState(dayjs(turnier.date));
    const [location, setLocation] = useState(turnier.location || '');
    const [description, setDescription] = useState(turnier.description || '');
    const [rules, setRules] = useState(turnier.rules || '');
    const [contact, setContact] = useState(turnier.contact || 'Turnierleitung');
    const [isPublished, setIsPublished] = useState(!!turnier.isPublished);

    // Tab 1: Groups State
    const [groups, setGroups] = useState(turnier.groups || []);
    const [newGroupName, setNewGroupName] = useState('');
    const [newTeamNameMap, setNewTeamNameMap] = useState({}); // { [groupIdx]: 'Teamname' }
    const [editingGroupId, setEditingGroupId] = useState(null);
    const [tempGroupName, setTempGroupName] = useState('');
    const [editingTeamId, setEditingTeamId] = useState(null); // { groupIdx, teamIdx }
    const [tempTeamName, setTempTeamName] = useState('');

    // Tab 2: Match State
    const [matches, setMatches] = useState(turnier.matches || []);
    const [matchRound, setMatchRound] = useState('Vorrunde');
    const [matchGroup, setMatchGroup] = useState('');
    const [matchTime, setMatchTime] = useState('10:00');
    const [matchCourt, setMatchCourt] = useState('Platz 1');
    const [matchTeamA, setMatchTeamA] = useState('');
    const [matchTeamB, setMatchTeamB] = useState('');
    const [editingMatchId, setEditingMatchId] = useState(null);
    const [scoreA, setScoreA] = useState('');
    const [scoreB, setScoreB] = useState('');

    // Match details editing (for inline editing of team names, round, time, court)
    const [editingMatchDetailsId, setEditingMatchDetailsId] = useState(null);
    const [editMatchForm, setEditMatchForm] = useState({ teamA: '', teamB: '', round: '', group: '', time: '', court: '' });

    // Tab 2: Generator State
    const [genStartTime, setGenStartTime] = useState('10:00');
    const [genDuration, setGenDuration] = useState(20); // in minutes
    const [genBreak, setGenBreak] = useState(10); // break in minutes

    // Tab 3: Catering State
    const DEFAULT_CATERING = [
        { name: 'Cola / Spezi', size: 'Flasche (0,5 l)', price: '2,00 €', category: 'Getränke', order: 10 },
        { name: 'A-Schorle, Sportiv', size: 'Flasche (0,5 l)', price: '2,00 €', category: 'Getränke', order: 20 },
        { name: 'Mineralwasser', size: 'Flasche (0,7 l)', price: '2,00 €', category: 'Getränke', order: 30 },
        { name: 'Bier', size: 'Flasche (0,5 l)', price: '2,50 €', category: 'Getränke', order: 40 },
        { name: 'Weinschorle', size: 'Glas', price: '2,50 €', category: 'Getränke', order: 50 },
        { name: 'Wein', size: 'Glas', price: '3,00 €', category: 'Getränke', order: 60 },
        { name: 'Sekt / Wein', size: 'Flasche', price: '9,00 €', category: 'Getränke', order: 70 },
        { name: 'Kaffee', size: 'Tasse', price: '2,00 €', category: 'Kaffee & Kuchen', order: 80 },
        { name: 'Aperol', size: '', price: '4,00 €', category: 'Getränke', order: 90 },
    ];
    const [catering, setCatering] = useState(turnier.catering || []);
    const [newItemName, setNewItemName] = useState('');
    const [newItemSize, setNewItemSize] = useState('');
    const [newItemPrice, setNewItemPrice] = useState('');
    const [newItemCategory, setNewItemCategory] = useState('Speisen');
    const [newItemDescription, setNewItemDescription] = useState('');
    const [newItemOrder, setNewItemOrder] = useState('0');
    const [editingPriceId, setEditingPriceId] = useState(null);
    const [editingPriceValue, setEditingPriceValue] = useState('');
    const [editingOrderId, setEditingOrderId] = useState(null);
    const [editingOrderValue, setEditingOrderValue] = useState('');
    const [editingSizeId, setEditingSizeId] = useState(null);
    const [editingSizeValue, setEditingSizeValue] = useState('');
    const [editingNameId, setEditingNameId] = useState(null);
    const [editingNameValue, setEditingNameValue] = useState('');

    // Tab 4: Sponsors State
    const [newSponsorName, setNewSponsorName] = useState('');
    const [newSponsorWebsite, setNewSponsorWebsite] = useState('');
    const [newSponsorLogo, setNewSponsorLogo] = useState(null);

    // Sync state with turnier prop when changed
    useEffect(() => {
        setName(turnier.name || '');
        setDate(dayjs(turnier.date));
        setLocation(turnier.location || '');
        setDescription(turnier.description || '');
        setRules(turnier.rules || '');
        setContact(turnier.contact || 'Turnierleitung');
        setIsPublished(!!turnier.isPublished);
        setGroups(turnier.groups || []);
        setMatches(turnier.matches || []);
        setCatering(turnier.catering || []);
    }, [turnier]);

    const handleAddCateringItem = async () => {
        if (!newItemName.trim()) {
            toast.error('Bitte Artikelnamen eingeben');
            return;
        }
        try {
            const res = await axios.post(`${API_BASE_URL}/api/gruemp/${turnier._id}/catering`, {
                name: newItemName,
                size: newItemSize,
                price: newItemPrice,
                category: newItemCategory,
                description: newItemDescription,
                order: Number(newItemOrder) || 0
            });
            setCatering(res.data.catering);
            setNewItemName('');
            setNewItemSize('');
            setNewItemPrice('');
            setNewItemDescription('');
            setNewItemOrder('0');
            toast.success('Artikel hinzugefügt');
            if (onUpdate) onUpdate();
        } catch (err) {
            toast.error('Fehler beim Hinzufügen des Artikels');
        }
    };

    const handleDeleteCateringItem = (itemId) => {
        triggerConfirm(
            'Verpflegungs-Artikel löschen',
            'Möchten Sie diesen Artikel wirklich löschen?',
            async () => {
                try {
                    const res = await axios.delete(`${API_BASE_URL}/api/gruemp/${turnier._id}/catering/${itemId}`, { withCredentials: true });
                    setCatering(res.data.catering);
                    toast.success('Artikel gelöscht');
                    if (onUpdate) onUpdate();
                } catch (err) {
                    toast.error('Fehler beim Löschen');
                }
            }
        );
    };

    const handleStartEditPrice = (item) => {
        setEditingPriceId(item._id);
        setEditingPriceValue(item.price || '');
    };

    const handleSavePrice = async (itemId) => {
        const updated = catering.map(i =>
            i._id === itemId ? { ...i, price: editingPriceValue } : i
        );
        try {
            const res = await axios.put(`${API_BASE_URL}/api/gruemp/${turnier._id}/catering`, { catering: updated }, { withCredentials: true });
            setCatering(res.data.catering);
            toast.success('Preis gespeichert');
            if (onUpdate) onUpdate();
        } catch (err) {
            toast.error('Fehler beim Speichern des Preises');
        } finally {
            setEditingPriceId(null);
            setEditingPriceValue('');
        }
    };

    const handleStartEditOrder = (item) => {
        setEditingOrderId(item._id);
        setEditingOrderValue(String(item.order || 0));
    };

    const handleSaveOrder = async (itemId) => {
        const updated = catering.map(i =>
            i._id === itemId ? { ...i, order: Number(editingOrderValue) || 0 } : i
        );
        try {
            const res = await axios.put(`${API_BASE_URL}/api/gruemp/${turnier._id}/catering`, { catering: updated }, { withCredentials: true });
            setCatering(res.data.catering);
            toast.success('Reihenfolge gespeichert');
            if (onUpdate) onUpdate();
        } catch (err) {
            toast.error('Fehler beim Speichern der Reihenfolge');
        } finally {
            setEditingOrderId(null);
            setEditingOrderValue('');
        }
    };

    const handleStartEditSize = (item) => {
        setEditingSizeId(item._id);
        setEditingSizeValue(item.size || '');
    };

    const handleSaveSize = async (itemId) => {
        const updated = catering.map(i =>
            i._id === itemId ? { ...i, size: editingSizeValue } : i
        );
        try {
            const res = await axios.put(`${API_BASE_URL}/api/gruemp/${turnier._id}/catering`, { catering: updated }, { withCredentials: true });
            setCatering(res.data.catering);
            toast.success('Menge/Einheit gespeichert');
            if (onUpdate) onUpdate();
        } catch (err) {
            toast.error('Fehler beim Speichern der Menge/Einheit');
        } finally {
            setEditingSizeId(null);
            setEditingSizeValue('');
        }
    };

    const handleStartEditName = (item) => {
        setEditingNameId(item._id);
        setEditingNameValue(item.name || '');
    };

    const handleSaveName = async (itemId) => {
        if (!editingNameValue.trim()) {
            toast.error('Name darf nicht leer sein');
            return;
        }
        const updated = catering.map(i =>
            i._id === itemId ? { ...i, name: editingNameValue } : i
        );
        try {
            const res = await axios.put(`${API_BASE_URL}/api/gruemp/${turnier._id}/catering`, { catering: updated }, { withCredentials: true });
            setCatering(res.data.catering);
            toast.success('Name gespeichert');
            if (onUpdate) onUpdate();
        } catch (err) {
            toast.error('Fehler beim Speichern des Namens');
        } finally {
            setEditingNameId(null);
            setEditingNameValue('');
        }
    };

    const handleLoadDefaultCatering = async () => {
        const merged = [...catering];
        DEFAULT_CATERING.forEach(def => {
            const exists = merged.some(i => i.name === def.name);
            if (!exists) merged.push(def);
        });
        try {
            const res = await axios.put(`${API_BASE_URL}/api/gruemp/${turnier._id}/catering`, { catering: merged }, { withCredentials: true });
            setCatering(res.data.catering);
            toast.success('Standard-Getränke eingefügt!');
            if (onUpdate) onUpdate();
        } catch (err) {
            toast.error('Fehler beim Einfügen der Standard-Getränke');
        }
    };

    const handleSaveDetails = () => {
        setIsSaving(true);
        const data = {
            name,
            date: date.toDate(),
            location,
            description,
            rules,
            contact,
            isPublished
        };

        axios.put(`${API_BASE_URL}/api/gruemp/${turnier._id}/details`, data, { withCredentials: true })
            .then(() => {
                toast.success("Turnierdetails gespeichert!");
                onUpdate();
            })
            .catch(err => {
                console.error(err);
                toast.error("Fehler beim Speichern: " + (err.response?.data?.message || err.message));
            })
            .finally(() => setIsSaving(false));
    };

    const handleSaveGroups = (updatedGroups) => {
        const targetGroups = updatedGroups || groups;
        axios.put(`${API_BASE_URL}/api/gruemp/${turnier._id}/groups`, { groups: targetGroups }, { withCredentials: true })
            .then(() => {
                toast.success("Gruppen & Mannschaften gespeichert!");
                onUpdate();
            })
            .catch(err => {
                console.error(err);
                toast.error("Fehler beim Speichern der Gruppen: " + (err.response?.data?.message || err.message));
            });
    };

    const startEditGroup = (groupIdx, currentName) => {
        setEditingGroupId(groupIdx);
        setTempGroupName(currentName);
    };

    const handleSaveGroupName = (groupIdx) => {
        if (!tempGroupName.trim()) return;
        const updated = [...groups];
        updated[groupIdx].name = tempGroupName.trim();
        setGroups(updated);
        setEditingGroupId(null);
        handleSaveGroups(updated);
    };

    const startEditTeam = (groupIdx, teamIdx, currentName) => {
        setEditingTeamId({ groupIdx, teamIdx });
        setTempTeamName(currentName);
    };

    const handleSaveTeamName = (groupIdx, teamIdx) => {
        if (!tempTeamName.trim()) return;
        const updated = [...groups];
        updated[groupIdx].teams[teamIdx] = tempTeamName.trim();
        setGroups(updated);
        setEditingTeamId(null);
        handleSaveGroups(updated);
    };

    const handleAddGroup = () => {
        if (!newGroupName.trim()) return;
        const updated = [...groups, { name: newGroupName.trim(), teams: [] }];
        setGroups(updated);
        setNewGroupName('');
        handleSaveGroups(updated);
    };

    const handleDeleteGroup = (groupIndex) => {
        triggerConfirm(
            'Gruppe löschen',
            'Möchten Sie diese Gruppe wirklich unwiderruflich löschen? Alle zugeordneten Teams gehen verloren.',
            () => {
                const updated = groups.filter((_, i) => i !== groupIndex);
                setGroups(updated);
                handleSaveGroups(updated);
            }
        );
    };

    const handleAddTeam = (groupIndex) => {
        const teamName = newTeamNameMap[groupIndex]?.trim();
        if (!teamName) return;

        const updated = [...groups];
        updated[groupIndex].teams.push(teamName);
        setGroups(updated);

        setNewTeamNameMap(prev => ({ ...prev, [groupIndex]: '' }));
        handleSaveGroups(updated);
    };

    const handleDeleteTeam = (groupIndex, teamIndex) => {
        const teamName = groups[groupIndex]?.teams[teamIndex] || 'dieses Team';
        triggerConfirm(
            'Team löschen',
            `Möchten Sie das Team "${teamName}" wirklich aus dieser Gruppe löschen?`,
            () => {
                const updated = [...groups];
                updated[groupIndex].teams.splice(teamIndex, 1);
                setGroups(updated);
                handleSaveGroups(updated);
            }
        );
    };

    const handleAddMatch = () => {
        if (!matchTeamA.trim() || !matchTeamB.trim()) {
            toast.warning("Bitte beide Teams eintragen.");
            return;
        }
        const data = {
            round: matchRound,
            group: matchGroup,
            time: matchTime,
            court: matchCourt,
            teamA: matchTeamA.trim(),
            teamB: matchTeamB.trim()
        };

        axios.post(`${API_BASE_URL}/api/gruemp/${turnier._id}/matches`, data, { withCredentials: true })
            .then(() => {
                toast.success("Spiel hinzugefügt!");
                setMatchTeamA('');
                setMatchTeamB('');
                onUpdate();
            })
            .catch(err => toast.error("Fehler beim Hinzufügen des Spiels"));
    };

    const handleDeleteMatch = (matchId) => {
        triggerConfirm(
            'Spiel löschen',
            'Möchten Sie dieses Spiel wirklich aus dem Spielplan löschen?',
            () => {
                axios.delete(`${API_BASE_URL}/api/gruemp/${turnier._id}/matches/${matchId}`, { withCredentials: true })
                    .then(() => {
                        toast.info("Spiel gelöscht.");
                        onUpdate();
                    })
                    .catch(err => toast.error("Fehler beim Löschen des Spiels"));
            }
        );
    };

    const handleStartEditScore = (match) => {
        setEditingMatchId(match._id);
        setScoreA(match.scoreA !== null ? match.scoreA.toString() : '');
        setScoreB(match.scoreB !== null ? match.scoreB.toString() : '');
    };

    const handleSaveScore = (matchId) => {
        const parsedA = parseInt(scoreA);
        const parsedB = parseInt(scoreB);
        if (isNaN(parsedA) || isNaN(parsedB)) {
            toast.warning("Bitte gültige Ergebnisse eintragen.");
            return;
        }

        axios.patch(`${API_BASE_URL}/api/gruemp/${turnier._id}/matches/${matchId}`, {
            scoreA: parsedA,
            scoreB: parsedB,
            status: 'Completed'
        }, { withCredentials: true })
            .then(() => {
                toast.success("Ergebnis eingetragen!");
                setEditingMatchId(null);
                onUpdate();
            })
            .catch(err => toast.error("Fehler beim Eintragen des Ergebnisses"));
    };

    const handleResetMatchStatus = (matchId) => {
        axios.patch(`${API_BASE_URL}/api/gruemp/${turnier._id}/matches/${matchId}`, {
            scoreA: null,
            scoreB: null,
            status: 'Scheduled'
        }, { withCredentials: true })
            .then(() => {
                toast.info("Spiel zurückgesetzt.");
                onUpdate();
            })
            .catch(err => toast.error("Fehler beim Zurücksetzen des Spiels"));
    };

    const handleStartEditMatchDetails = (match) => {
        setEditingMatchDetailsId(match._id);
        setEditMatchForm({
            teamA: match.teamA || '',
            teamB: match.teamB || '',
            round: match.round || '',
            group: match.group || '',
            time: match.time || '',
            court: match.court || ''
        });
        // Close score editing if open
        setEditingMatchId(null);
    };

    const handleSaveMatchDetails = (matchId) => {
        if (!editMatchForm.teamA.trim() || !editMatchForm.teamB.trim()) {
            toast.warning('Bitte beide Team-Namen eintragen.');
            return;
        }
        axios.patch(`${API_BASE_URL}/api/gruemp/${turnier._id}/matches/${matchId}`, {
            teamA: editMatchForm.teamA.trim(),
            teamB: editMatchForm.teamB.trim(),
            round: editMatchForm.round.trim(),
            group: editMatchForm.group.trim(),
            time: editMatchForm.time.trim(),
            court: editMatchForm.court.trim()
        }, { withCredentials: true })
            .then(() => {
                toast.success('Spiel-Details gespeichert!');
                setEditingMatchDetailsId(null);
                onUpdate();
            })
            .catch(err => toast.error('Fehler beim Speichern der Spiel-Details: ' + (err.response?.data?.message || err.message)));
    };

    // Helper to calculate standings of a group dynamically
    const calculateStandingsForGroup = (groupName) => {
        const groupObj = groups.find(g => g.name === groupName);
        if (!groupObj) return [];

        const teamStats = {};
        groupObj.teams.forEach(team => {
            teamStats[team] = {
                team,
                played: 0,
                won: 0,
                drawn: 0,
                lost: 0,
                goalsFor: 0,
                goalsAgainst: 0,
                points: 0
            };
        });

        const groupMatches = matches.filter(m => 
            m.group === groupName && m.status === 'Completed' && 
            m.scoreA !== null && m.scoreB !== null
        );

        groupMatches.forEach(m => {
            const statsA = teamStats[m.teamA];
            const statsB = teamStats[m.teamB];

            if (statsA && statsB) {
                statsA.played += 1;
                statsB.played += 1;
                statsA.goalsFor += m.scoreA;
                statsA.goalsAgainst += m.scoreB;
                statsB.goalsFor += m.scoreB;
                statsB.goalsAgainst += m.scoreA;

                if (m.scoreA > m.scoreB) {
                    statsA.won += 1;
                    statsA.points += 3;
                    statsB.lost += 1;
                } else if (m.scoreA < m.scoreB) {
                    statsB.won += 1;
                    statsB.points += 3;
                    statsA.lost += 1;
                } else {
                    statsA.drawn += 1;
                    statsA.points += 1;
                    statsB.drawn += 1;
                    statsB.points += 1;
                }
            }
        });

        return Object.values(teamStats).sort((a, b) => {
            if (b.points !== a.points) return b.points - a.points;
            const diffA = a.goalsFor - a.goalsAgainst;
            const diffB = b.goalsFor - b.goalsAgainst;
            if (diffB !== diffA) return diffB - diffA;
            if (b.goalsFor !== a.goalsFor) return b.goalsFor - a.goalsFor;
            return a.team.localeCompare(b.team);
        });
    };

    // Helper to resolve placeholders like "1. Gruppe A" to the actual team name
    const resolveTeamName = (teamName) => {
        if (!teamName) return '';
        const matchGroup = teamName.match(/^(\d+)\.\s*Gruppe\s+(.+)$/i);
        if (matchGroup) {
            const position = parseInt(matchGroup[1]) - 1;
            const groupName = matchGroup[2].trim();
            const standings = calculateStandingsForGroup(groupName);
            if (standings && standings[position]) {
                return `${standings[position].team} (${teamName})`;
            }
        }
        return teamName;
    };

    const handleDeleteAllMatches = () => {
        triggerConfirm(
            'Alle Spiele löschen',
            'Möchten Sie wirklich alle Spiele aus dem Spielplan unwiderruflich löschen?',
            () => {
                axios.delete(`${API_BASE_URL}/api/gruemp/${turnier._id}/matches`, { withCredentials: true })
                    .then(() => {
                        toast.success("Alle Spiele gelöscht.");
                        onUpdate();
                    })
                    .catch(err => toast.error("Fehler beim Löschen der Spiele: " + (err.response?.data?.message || err.message)));
            }
        );
    };

    const runGenerationLogic = () => {
        // Generate rounds for each group
        const groupRounds = {};
        let maxRounds = 0;

        groups.forEach(group => {
            let list = [...group.teams];
            if (list.length % 2 !== 0) {
                list.push("SPIELFREI");
            }

            const numTeams = list.length;
            const roundsCount = numTeams - 1;
            const half = numTeams / 2;
            const rounds = [];

            let teamIndices = Array.from({ length: numTeams }, (_, i) => i);

            for (let r = 0; r < roundsCount; r++) {
                const roundMatches = [];
                for (let i = 0; i < half; i++) {
                    const t1 = list[teamIndices[i]];
                    const t2 = list[teamIndices[numTeams - 1 - i]];
                    if (t1 !== "SPIELFREI" && t2 !== "SPIELFREI") {
                        roundMatches.push({ teamA: t1, teamB: t2 });
                    }
                }
                rounds.push(roundMatches);
                teamIndices = [teamIndices[0], teamIndices[numTeams - 1], ...teamIndices.slice(1, numTeams - 1)];
            }
            groupRounds[group.name] = rounds;
            if (rounds.length > maxRounds) maxRounds = rounds.length;
        });

        // 6 Courts (Plätze) configuration
        const numCourts = 6;
        const courtsList = Array.from({ length: numCourts }, (_, i) => `Platz ${i + 1}`);

        // Divide groups into batches of 3, playing concurrently.
        // Batch 1: A, B, C; Batch 2: D, E, F (if using 6 groups)
        const batchSize = Math.floor(numCourts / 2) || 1;
        const groupNames = groups.map(g => g.name);
        const batches = [];
        for (let i = 0; i < groupNames.length; i += batchSize) {
            batches.push(groupNames.slice(i, i + batchSize));
        }

        const durationMin = parseInt(genDuration) || 20;
        const breakMin = parseInt(genBreak) || 10;
        const slotInterval = durationMin + breakMin; // 30 minutes

        let currentHour = parseInt(genStartTime.split(':')[0]) || 10;
        let currentMin = parseInt(genStartTime.split(':')[1]) || 0;

        const formatTime = (h, m) => {
            return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
        };

        const getNextTimeStr = (h, m, duration) => {
            let nextMin = m + duration;
            let nextHour = h;
            if (nextMin >= 60) {
                nextHour += Math.floor(nextMin / 60);
                nextMin = nextMin % 60;
            }
            return formatTime(nextHour, nextMin);
        };

        const incrementTime = () => {
            currentMin += slotInterval;
            if (currentMin >= 60) {
                currentHour += Math.floor(currentMin / 60);
                currentMin = currentMin % 60;
            }
        };

        const generated = [];

        // Build a global pool: interleave matches round-by-round across all groups
        // so we don't exhaust one group's rounds before starting another.
        // E.g. [A-R1, B-R1, C-R1, D-R1, E-R1, F-R1, A-R2, B-R2, ...]
        const pool = [];
        for (let r = 0; r < maxRounds; r++) {
            groups.forEach(group => {
                const groupR = groupRounds[group.name];
                if (groupR && groupR[r]) {
                    groupR[r].forEach(match => {
                        pool.push({
                            group: group.name,
                            teamA: match.teamA,
                            teamB: match.teamB
                        });
                    });
                }
            });
        }

        // Fill 6-court time slots greedily from the pool.
        // Within each slot, ensure no team plays twice (conflict check).
        while (pool.length > 0) {
            const timeStr = `${formatTime(currentHour, currentMin)} - ${getNextTimeStr(currentHour, currentMin, durationMin)}`;
            const slotMatches = [];
            const teamsInSlot = new Set();
            const remaining = [];

            for (const match of pool) {
                if (slotMatches.length >= numCourts) {
                    remaining.push(match);
                    continue;
                }
                // Skip if either team already plays in this slot
                if (teamsInSlot.has(match.teamA) || teamsInSlot.has(match.teamB)) {
                    remaining.push(match);
                    continue;
                }
                slotMatches.push(match);
                teamsInSlot.add(match.teamA);
                teamsInSlot.add(match.teamB);
            }

            // If we couldn't schedule any match (all remaining teams conflict),
            // force-schedule the first match to avoid an infinite loop.
            if (slotMatches.length === 0 && remaining.length > 0) {
                slotMatches.push(remaining.shift());
            }

            slotMatches.forEach((match, idx) => {
                generated.push({
                    round: "Vorrunde",
                    group: match.group,
                    time: timeStr,
                    court: courtsList[idx],
                    teamA: match.teamA,
                    teamB: match.teamB
                });
            });

            // remaining already contains only unscheduled matches
            pool.length = 0;
            pool.push(...remaining);

            incrementTime();
        }

        axios.post(`${API_BASE_URL}/api/gruemp/${turnier._id}/matches/bulk`, { matches: generated }, { withCredentials: true })
            .then(() => {
                toast.success(`${generated.length} Vorrundenspiele für alle Gruppen generiert!`);
                onUpdate();
            })
            .catch(err => {
                console.error(err);
                toast.error("Fehler beim automatischen Generieren des Spielplans");
            });
    };

    const handleGenerateSchedule = () => {
        if (groups.length === 0) {
            toast.warning("Bitte legen Sie zuerst Gruppen und Teams an.");
            return;
        }

        if (matches.length > 0) {
            triggerConfirm(
                'Spielplan neu generieren?',
                'Es existieren bereits Spiele im Spielplan. Möchten Sie alle bestehenden Spiele löschen und den Vorrunden-Spielplan neu generieren?',
                () => {
                    axios.delete(`${API_BASE_URL}/api/gruemp/${turnier._id}/matches`, { withCredentials: true })
                        .then(() => {
                            runGenerationLogic();
                        })
                        .catch(err => toast.error("Fehler beim Löschen der alten Spiele: " + (err.response?.data?.message || err.message)));
                }
            );
        } else {
            runGenerationLogic();
        }
    };

    // KO-Runde Stage Generator with Placeholder matches and 30min pause after Vorrunde
    const handleGenerateKoMatches = () => {
        let koStartHour = 14;
        let koStartMin = 0;

        const vorrundeMatches = matches.filter(m => m.round === "Vorrunde");
        if (vorrundeMatches.length > 0) {
            let latestEndVal = 0;
            vorrundeMatches.forEach(m => {
                let timeParts = m.time.split('-');
                let endPart = timeParts[1] ? timeParts[1].trim() : timeParts[0].trim();
                let hr = parseInt(endPart.split(':')[0]) || 0;
                let mn = parseInt(endPart.split(':')[1]) || 0;
                let totalMins = hr * 60 + mn;
                if (totalMins > latestEndVal) {
                    latestEndVal = totalMins;
                }
            });
            if (latestEndVal > 0) {
                // Add 30 minutes pause before KO matches start
                let startMins = latestEndVal + 30;
                koStartHour = Math.floor(startMins / 60);
                koStartMin = startMins % 60;
            }
        }

        const durationMin = parseInt(genDuration) || 20;
        const breakMin = parseInt(genBreak) || 10;
        const slotInterval = durationMin + breakMin; // 30 minutes

        const getKoSlotTimeRange = (slotIndex) => {
            let startMins = (koStartHour * 60 + koStartMin) + (slotIndex * slotInterval);
            let h = Math.floor(startMins / 60);
            let m = startMins % 60;
            
            let endMins = startMins + durationMin;
            let eh = Math.floor(endMins / 60);
            let em = endMins % 60;

            return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')} - ${eh.toString().padStart(2, '0')}:${em.toString().padStart(2, '0')}`;
        };


        const koMatches = [
            // Slot 0: Alle 4 Halbfinals zeitgleich auf Platz 1, 2, 5, 6
            {
                round: "Halbfinale (Herren)",
                group: "",
                time: getKoSlotTimeRange(0),
                court: "Platz 1",
                teamA: "1. Gruppe A",
                teamB: "1. Gruppe B"
            },
            {
                round: "Halbfinale (Herren)",
                group: "",
                time: getKoSlotTimeRange(0),
                court: "Platz 2",
                teamA: "1. Gruppe C",
                teamB: "1. Gruppe D"
            },
            {
                round: "Halbfinale (Mixed)",
                group: "",
                time: getKoSlotTimeRange(0),
                court: "Platz 5",
                teamA: "1. Gruppe E",
                teamB: "2. Gruppe F"
            },
            {
                round: "Halbfinale (Mixed)",
                group: "",
                time: getKoSlotTimeRange(0),
                court: "Platz 6",
                teamA: "1. Gruppe F",
                teamB: "2. Gruppe E"
            },
            // Slot 1: Spiele um Platz 3 zeitgleich auf Platz 1 und 2
            {
                round: "Spiel um Platz 3 (Herren)",
                group: "",
                time: getKoSlotTimeRange(1),
                court: "Platz 1",
                teamA: "Verlierer Herren HF 1",
                teamB: "Verlierer Herren HF 2"
            },
            {
                round: "Spiel um Platz 3 (Mixed)",
                group: "",
                time: getKoSlotTimeRange(1),
                court: "Platz 2",
                teamA: "Verlierer Mixed HF 1",
                teamB: "Verlierer Mixed HF 2"
            },
            // Slot 2: Finals zeitgleich auf Platz 5 und 6
            {
                round: "Finale (Herren)",
                group: "",
                time: getKoSlotTimeRange(2),
                court: "Platz 5",
                teamA: "Sieger Herren HF 1",
                teamB: "Sieger Herren HF 2"
            },
            {
                round: "Finale (Mixed)",
                group: "",
                time: getKoSlotTimeRange(2),
                court: "Platz 6",
                teamA: "Sieger Mixed HF 1",
                teamB: "Sieger Mixed HF 2"
            }
        ];

        axios.post(`${API_BASE_URL}/api/gruemp/${turnier._id}/matches/bulk`, { matches: koMatches }, { withCredentials: true })
            .then(() => {
                toast.success("KO-Spiele (Platzhalter) mit berechneten Zeiten erfolgreich hinzugefügt!");
                onUpdate();
            })
            .catch(err => {
                console.error(err);
                toast.error("Fehler beim Generieren der KO-Spiele");
            });
    };



    const handleAddSponsor = (e) => {
        e.preventDefault();
        if (!newSponsorName.trim()) return;

        const formData = new FormData();
        formData.append('name', newSponsorName.trim());
        formData.append('website', newSponsorWebsite.trim());
        if (newSponsorLogo) {
            formData.append('logo', newSponsorLogo);
        }

        axios.post(`${API_BASE_URL}/api/gruemp/${turnier._id}/sponsors`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            withCredentials: true
        })
            .then(() => {
                toast.success("Sponsor hinzugefügt!");
                setNewSponsorName('');
                setNewSponsorWebsite('');
                setNewSponsorLogo(null);
                onUpdate();
            })
            .catch(err => toast.error("Fehler beim Hinzufügen des Sponsors"));
    };

    const handleDeleteSponsor = (sponsorId) => {
        triggerConfirm(
            'Sponsor löschen',
            'Möchten Sie diesen Sponsor wirklich löschen?',
            () => {
                axios.delete(`${API_BASE_URL}/api/gruemp/${turnier._id}/sponsors/${sponsorId}`, { withCredentials: true })
                    .then(() => {
                        toast.info("Sponsor gelöscht.");
                        onUpdate();
                    })
                    .catch(err => toast.error("Fehler beim Löschen des Sponsors"));
            }
        );
    };

    const handlePrintQr = () => {
        const printWindow = window.open('', '_blank');
        const year = new Date(turnier.date).getFullYear();
        const shareUrl = `${window.location.origin}/gt/${year}`;
        const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(shareUrl)}`;
        
        printWindow.document.write(`
            <html>
                <head>
                    <title>QR-Code Drucken</title>
                    <style>
                        body { font-family: sans-serif; text-align: center; padding: 50px; }
                        .container { max-width: 500px; margin: auto; border: 2px solid #ccc; padding: 30px; border-radius: 15px; }
                        img { max-width: 100%; height: auto; margin-bottom: 20px; }
                        h1 { color: #d32f2f; }
                        p { font-size: 1.1em; color: #555; }
                    </style>
                </head>
                <body onload="window.print(); window.close();">
                    <div class="container">
                        <h1>TCO Grümpelturnier LIVE</h1>
                        <img src="${qrUrl}" alt="QR-Code"/>
                        <p>Scanne diesen Code, um den aktuellen Spielplan und alle Live-Ergebnisse zu sehen!</p>
                    </div>
                </body>
            </html>
        `);
        printWindow.document.close();
    };

    const shareUrl = `${window.location.origin}/gt/${new Date(turnier.date).getFullYear()}`;

    return (
        <Paper variant="outlined" sx={{ borderRadius: '12px', overflow: 'hidden' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#fafafa' }}>
                <Tabs value={activeTab} onChange={(e, val) => setActiveTab(val)}>
                    <Tab label="Details & Infos" icon={<FaInfoCircle />} iconPosition="start" />
                    <Tab label="Gruppen & Teams" icon={<FaTrophy />} iconPosition="start" />
                    <Tab label="Spielplan & Ergebnisse" icon={<FaCalendarAlt />} iconPosition="start" />
                    <Tab label="Speisen & Getränke" icon={<FaUtensils />} iconPosition="start" />
                    <Tab label="Sponsoren" icon={<FaHandshake />} iconPosition="start" />
                    <Tab label="QR-Code / Live-Link" icon={<FaQrcode />} iconPosition="start" />
                </Tabs>
            </Box>

            <Box p={3}>
                {/* TAB 0: DETAILS & INFOS */}
                {activeTab === 0 && (
                    <Stack spacing={3}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField 
                                    label="Name des Turniers" 
                                    fullWidth 
                                    value={name} 
                                    onChange={(e) => setName(e.target.value)} 
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField 
                                    label="Austragungsort" 
                                    fullWidth 
                                    value={location} 
                                    onChange={(e) => setLocation(e.target.value)} 
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <DatePicker 
                                    label="Datum"
                                    value={date}
                                    onChange={(val) => setDate(val)}
                                    slotProps={{ textField: { fullWidth: true } }}
                                    format="DD.MM.YYYY"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField 
                                    label="Ansprechpartner" 
                                    fullWidth 
                                    value={contact} 
                                    onChange={(e) => setContact(e.target.value)} 
                                />
                            </Grid>
                        </Grid>

                        <TextField 
                            label="Beschreibung" 
                            fullWidth 
                            multiline 
                            rows={3}
                            value={description} 
                            onChange={(e) => setDescription(e.target.value)} 
                        />

                        <TextField 
                            label="Regeln & Spielordnung" 
                            fullWidth 
                            multiline 
                            rows={6}
                            value={rules} 
                            onChange={(e) => setRules(e.target.value)} 
                            placeholder="Z.B.: Spieldauer: 15 Minuten. 5 gegen 5. Bei Punktgleichheit entscheidet das Torverhältnis..."
                        />

                        <FormControlLabel
                            control={
                                <Switch 
                                    checked={isPublished} 
                                    onChange={(e) => setIsPublished(e.target.checked)} 
                                    color="success"
                                />
                            }
                            label="Turnierseite öffentlich freischalten (ohne Login unter /gt/:jahr erreichbar)"
                        />

                        <Button 
                            variant="contained" 
                            startIcon={<FaSave />} 
                            onClick={handleSaveDetails} 
                            disabled={isSaving}
                            sx={{ alignSelf: 'flex-start' }}
                        >
                            Turnier-Details speichern
                        </Button>
                    </Stack>
                )}

                {/* TAB 1: GRUPPEN & TEAMS */}
                {activeTab === 1 && (
                    <Stack spacing={3}>
                        <Box display="flex" gap={2}>
                            <TextField 
                                label="Name der neuen Gruppe" 
                                value={newGroupName} 
                                onChange={(e) => setNewGroupName(e.target.value)} 
                                placeholder="Z.B. Gruppe A"
                            />
                            <Button variant="contained" startIcon={<FaPlus />} onClick={handleAddGroup}>
                                Gruppe anlegen
                            </Button>
                        </Box>

                        <Grid container spacing={3}>
                            {groups.map((group, groupIdx) => (
                                <Grid item xs={12} sm={6} key={groupIdx}>
                                    <Card variant="outlined" sx={{ borderRadius: '12px' }}>
                                        <CardContent>
                                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={1}>
                                                {editingGroupId === groupIdx ? (
                                                    <Box display="flex" gap={1} flexGrow={1} alignItems="center">
                                                        <TextField
                                                            size="small"
                                                            value={tempGroupName}
                                                            onChange={(e) => setTempGroupName(e.target.value)}
                                                            onKeyPress={(e) => e.key === 'Enter' && handleSaveGroupName(groupIdx)}
                                                            autoFocus
                                                            sx={{ flexGrow: 1 }}
                                                        />
                                                        <IconButton size="small" color="success" onClick={() => handleSaveGroupName(groupIdx)}>
                                                            <FaCheck size={14} />
                                                        </IconButton>
                                                        <IconButton size="small" onClick={() => setEditingGroupId(null)}>
                                                            <FaTimes size={14} />
                                                        </IconButton>
                                                    </Box>
                                                ) : (
                                                    <Box display="flex" alignItems="center" gap={1} flexGrow={1}>
                                                        <Typography variant="h6" fontWeight="bold">
                                                            {group.name}
                                                        </Typography>
                                                        <IconButton size="small" color="primary" onClick={() => startEditGroup(groupIdx, group.name)}>
                                                            <FaEdit size={14} />
                                                        </IconButton>
                                                    </Box>
                                                )}
                                                <IconButton color="error" onClick={() => handleDeleteGroup(groupIdx)}>
                                                    <FaTrash size={16} />
                                                </IconButton>
                                            </Box>
                                            
                                            <Stack spacing={1} mb={2}>
                                                {group.teams.length === 0 ? (
                                                    <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                                                        Noch keine Teams eingetragen
                                                    </Typography>
                                                ) : (
                                                    group.teams.map((team, teamIdx) => {
                                                        const isEditingTeam = editingTeamId?.groupIdx === groupIdx && editingTeamId?.teamIdx === teamIdx;
                                                        return (
                                                            <Box 
                                                                key={teamIdx} 
                                                                display="flex" 
                                                                justifyContent="space-between" 
                                                                alignItems="center"
                                                                bgcolor="#f5f5f5"
                                                                p={1}
                                                                borderRadius="6px"
                                                                gap={1}
                                                            >
                                                                {isEditingTeam ? (
                                                                    <Box display="flex" gap={1} flexGrow={1} alignItems="center">
                                                                        <TextField
                                                                            size="small"
                                                                            value={tempTeamName}
                                                                            onChange={(e) => setTempTeamName(e.target.value)}
                                                                            onKeyPress={(e) => e.key === 'Enter' && handleSaveTeamName(groupIdx, teamIdx)}
                                                                            autoFocus
                                                                            sx={{ flexGrow: 1, bgcolor: 'white', borderRadius: '4px' }}
                                                                            inputProps={{ style: { padding: '4px 8px', fontSize: '0.875rem' } }}
                                                                        />
                                                                        <IconButton size="small" color="success" onClick={() => handleSaveTeamName(groupIdx, teamIdx)}>
                                                                            <FaCheck size={12} />
                                                                        </IconButton>
                                                                        <IconButton size="small" onClick={() => setEditingTeamId(null)}>
                                                                            <FaTimes size={12} />
                                                                        </IconButton>
                                                                    </Box>
                                                                ) : (
                                                                    <Box display="flex" alignItems="center" gap={1} flexGrow={1}>
                                                                        <Typography variant="body2" fontWeight="bold">{team}</Typography>
                                                                        <IconButton size="small" color="primary" onClick={() => startEditTeam(groupIdx, teamIdx, team)}>
                                                                            <FaEdit size={10} />
                                                                        </IconButton>
                                                                    </Box>
                                                                )}
                                                                {!isEditingTeam && (
                                                                    <IconButton size="small" color="error" onClick={() => handleDeleteTeam(groupIdx, teamIdx)}>
                                                                        <FaTimes size={12} />
                                                                    </IconButton>
                                                                )}
                                                            </Box>
                                                        );
                                                    })
                                                )}
                                            </Stack>

                                            <Box display="flex" gap={1}>
                                                <TextField 
                                                    label="Neuer Mannschaftsname" 
                                                    size="small"
                                                    value={newTeamNameMap[groupIdx] || ''}
                                                    onChange={(e) => setNewTeamNameMap({
                                                        ...newTeamNameMap,
                                                        [groupIdx]: e.target.value
                                                    })}
                                                    onKeyPress={(e) => e.key === 'Enter' && handleAddTeam(groupIdx)}
                                                />
                                                <Button size="small" variant="outlined" startIcon={<FaPlus />} onClick={() => handleAddTeam(groupIdx)}>
                                                    Hinzufügen
                                                </Button>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Stack>
                )}

                {/* TAB 2: SPIELPLAN & ERGEBNISSE */}
                {activeTab === 2 && (
                    <Stack spacing={4}>
                        {/* Match Creator Form */}
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: '12px', bgcolor: '#fbfbfb' }}>
                            <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                                Manuell Spiel hinzufügen
                            </Typography>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={6} sm={2}>
                                    <TextField 
                                        label="Runde" 
                                        value={matchRound} 
                                        onChange={(e) => setMatchRound(e.target.value)} 
                                    />
                                </Grid>
                                <Grid item xs={6} sm={2}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Gruppe</InputLabel>
                                        <Select
                                            value={matchGroup}
                                            label="Gruppe"
                                            onChange={(e) => setMatchGroup(e.target.value)}
                                        >
                                            <MenuItem value=""><em>Keine</em></MenuItem>
                                            {groups.map(g => (
                                                <MenuItem key={g.name} value={g.name}>{g.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6} sm={1.5}>
                                    <TextField 
                                        label="Uhrzeit" 
                                        value={matchTime} 
                                        onChange={(e) => setMatchTime(e.target.value)} 
                                    />
                                </Grid>
                                <Grid item xs={6} sm={1.5}>
                                    <TextField 
                                        label="Platz" 
                                        value={matchCourt} 
                                        onChange={(e) => setMatchCourt(e.target.value)} 
                                    />
                                </Grid>
                                <Grid item xs={12} sm={2}>
                                    <TextField 
                                        label="Team A" 
                                        value={matchTeamA} 
                                        onChange={(e) => setMatchTeamA(e.target.value)} 
                                    />
                                </Grid>
                                <Grid item xs={12} sm={2}>
                                    <TextField 
                                        label="Team B" 
                                        value={matchTeamB} 
                                        onChange={(e) => setMatchTeamB(e.target.value)} 
                                    />
                                </Grid>
                                <Grid item xs={12} sm={1}>
                                    <Button variant="contained" onClick={handleAddMatch}>Hinzufügen</Button>
                                </Grid>
                            </Grid>
                        </Paper>

                        {/* Automatic generator */}
                        {groups.length > 0 && (
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: '12px', bgcolor: 'rgba(211, 47, 47, 0.03)' }}>
                                <Typography variant="subtitle1" fontWeight="bold" color="primary" mb={2}>
                                    Spielplan-Assistent (Automatische Generierung auf 6 Plätzen)
                                </Typography>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={6} sm={2}>
                                        <TextField 
                                            label="Startzeit" 
                                            value={genStartTime} 
                                            onChange={(e) => setGenStartTime(e.target.value)} 
                                            helperText="Z.B. 10:00"
                                        />
                                    </Grid>
                                    <Grid item xs={6} sm={2.5}>
                                        <TextField 
                                            label="Spieldauer (min)" 
                                            type="number"
                                            value={genDuration} 
                                            onChange={(e) => setGenDuration(e.target.value)} 
                                            helperText="Reine Spielzeit"
                                        />
                                    </Grid>
                                    <Grid item xs={6} sm={2.5}>
                                        <TextField 
                                            label="Pause (min)" 
                                            type="number"
                                            value={genBreak} 
                                            onChange={(e) => setGenBreak(e.target.value)} 
                                            helperText="Zeit zwischen Spielen"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={5}>
                                        <Stack direction="row" spacing={2}>
                                            <Button variant="contained" color="primary" startIcon={<FaPlay />} onClick={handleGenerateSchedule}>
                                                Vorrunde generieren (6 Plätze)
                                            </Button>
                                            <Button variant="outlined" color="primary" startIcon={<FaTrophy />} onClick={handleGenerateKoMatches}>
                                                KO-Runde generieren
                                            </Button>
                                        </Stack>
                                    </Grid>
                                </Grid>
                            </Paper>
                        )}

                        {/* Match List */}
                        <Box>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    Spiele-Liste ({matches.length} Spiele)
                                </Typography>
                                {matches.length > 0 && (
                                    <Button 
                                        variant="outlined" 
                                        color="error" 
                                        size="small" 
                                        startIcon={<FaTrash />} 
                                        onClick={handleDeleteAllMatches}
                                    >
                                        Alle Spiele löschen
                                    </Button>
                                )}
                            </Box>
                            {matches.length === 0 ? (
                                <Alert severity="info">Noch keine Spiele im Spielplan eingetragen.</Alert>
                            ) : (
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ bgcolor: '#fafafa' }}>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Uhrzeit</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Runde / Gruppe</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Platz</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Begegnung</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', width: '20%' }}>Ergebnis / Status</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>Aktionen</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {matches
                                                .sort((a, b) => a.time.localeCompare(b.time))
                                                .map((match) => (
                                                    <React.Fragment key={match._id}>
                                                        <TableRow hover sx={{ bgcolor: editingMatchDetailsId === match._id ? 'rgba(25,118,210,0.04)' : undefined }}>
                                                            <TableCell>{match.time}</TableCell>
                                                            <TableCell>
                                                                {match.round} {match.group && `(${match.group})`}
                                                            </TableCell>
                                                            <TableCell>{match.court}</TableCell>
                                                            <TableCell sx={{ fontWeight: 'bold' }}>
                                                                {resolveTeamName(match.teamA)} vs. {resolveTeamName(match.teamB)}
                                                            </TableCell>
                                                            <TableCell>
                                                                {editingMatchId === match._id ? (
                                                                    <Box display="flex" gap={1} alignItems="center">
                                                                        <TextField 
                                                                            size="small" 
                                                                            sx={{ width: 50 }} 
                                                                            value={scoreA}
                                                                            onChange={(e) => setScoreA(e.target.value)}
                                                                        />
                                                                        <Typography>:</Typography>
                                                                        <TextField 
                                                                            size="small" 
                                                                            sx={{ width: 50 }} 
                                                                            value={scoreB}
                                                                            onChange={(e) => setScoreB(e.target.value)}
                                                                        />
                                                                        <IconButton color="success" size="small" onClick={() => handleSaveScore(match._id)}>
                                                                            <FaCheck />
                                                                        </IconButton>
                                                                        <IconButton color="error" size="small" onClick={() => setEditingMatchId(null)}>
                                                                            <FaTimes />
                                                                        </IconButton>
                                                                    </Box>
                                                                ) : (
                                                                    <Box>
                                                                        {match.status === 'Completed' ? (
                                                                            <Chip 
                                                                                label={`${match.scoreA}:${match.scoreB}`} 
                                                                                color="success" 
                                                                                onClick={() => handleStartEditScore(match)}
                                                                            />
                                                                        ) : (
                                                                            <Button 
                                                                                size="small" 
                                                                                variant="outlined" 
                                                                                onClick={() => handleStartEditScore(match)}
                                                                            >
                                                                                Ergebnis eintragen
                                                                            </Button>
                                                                        )}
                                                                    </Box>
                                                                )}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Box display="flex" gap={1} alignItems="center">
                                                                    <IconButton 
                                                                        size="small" 
                                                                        color="primary"
                                                                        title="Spiel-Details bearbeiten"
                                                                        onClick={() => editingMatchDetailsId === match._id ? setEditingMatchDetailsId(null) : handleStartEditMatchDetails(match)}
                                                                    >
                                                                        <FaEdit size={14} />
                                                                    </IconButton>
                                                                    {match.status === 'Completed' && (
                                                                        <Button 
                                                                            size="small" 
                                                                            variant="text" 
                                                                            color="warning" 
                                                                            onClick={() => handleResetMatchStatus(match._id)}
                                                                        >
                                                                            Zurücksetzen
                                                                        </Button>
                                                                    )}
                                                                    <IconButton color="error" size="small" onClick={() => handleDeleteMatch(match._id)}>
                                                                        <FaTrash size={14} />
                                                                    </IconButton>
                                                                </Box>
                                                            </TableCell>
                                                        </TableRow>
                                                        {/* Inline Edit Row */}
                                                        {editingMatchDetailsId === match._id && (
                                                            <TableRow>
                                                                <TableCell colSpan={6} sx={{ bgcolor: 'rgba(25,118,210,0.03)', borderBottom: '2px solid rgba(25,118,210,0.2)', pb: 2 }}>
                                                                    <Box sx={{ p: 1.5 }}>
                                                                        <Typography variant="subtitle2" fontWeight="bold" color="primary" mb={1.5}>
                                                                            ✏️ Spiel bearbeiten
                                                                        </Typography>
                                                                        <Grid container spacing={1.5} alignItems="center">
                                                                            <Grid item xs={12} sm={6} md={3}>
                                                                                <TextField
                                                                                    label="Team A"
                                                                                    size="small"
                                                                                    fullWidth
                                                                                    value={editMatchForm.teamA}
                                                                                    onChange={(e) => setEditMatchForm(f => ({ ...f, teamA: e.target.value }))}
                                                                                    helperText="z.B. 1. Gruppe A"
                                                                                />
                                                                            </Grid>
                                                                            <Grid item xs={12} sm={6} md={3}>
                                                                                <TextField
                                                                                    label="Team B"
                                                                                    size="small"
                                                                                    fullWidth
                                                                                    value={editMatchForm.teamB}
                                                                                    onChange={(e) => setEditMatchForm(f => ({ ...f, teamB: e.target.value }))}
                                                                                    helperText="z.B. 2. Gruppe B"
                                                                                />
                                                                            </Grid>
                                                                            <Grid item xs={6} sm={4} md={2}>
                                                                                <TextField
                                                                                    label="Runde"
                                                                                    size="small"
                                                                                    fullWidth
                                                                                    value={editMatchForm.round}
                                                                                    onChange={(e) => setEditMatchForm(f => ({ ...f, round: e.target.value }))}
                                                                                />
                                                                            </Grid>
                                                                            <Grid item xs={6} sm={4} md={1.5}>
                                                                                <TextField
                                                                                    label="Uhrzeit"
                                                                                    size="small"
                                                                                    fullWidth
                                                                                    value={editMatchForm.time}
                                                                                    onChange={(e) => setEditMatchForm(f => ({ ...f, time: e.target.value }))}
                                                                                />
                                                                            </Grid>
                                                                            <Grid item xs={6} sm={4} md={1.5}>
                                                                                <TextField
                                                                                    label="Platz"
                                                                                    size="small"
                                                                                    fullWidth
                                                                                    value={editMatchForm.court}
                                                                                    onChange={(e) => setEditMatchForm(f => ({ ...f, court: e.target.value }))}
                                                                                />
                                                                            </Grid>
                                                                            <Grid item xs={6} sm={4} md={1.5}>
                                                                                <FormControl size="small" fullWidth>
                                                                                    <InputLabel>Gruppe</InputLabel>
                                                                                    <Select
                                                                                        value={editMatchForm.group}
                                                                                        label="Gruppe"
                                                                                        onChange={(e) => setEditMatchForm(f => ({ ...f, group: e.target.value }))}
                                                                                    >
                                                                                        <MenuItem value=""><em>Keine</em></MenuItem>
                                                                                        {groups.map(g => (
                                                                                            <MenuItem key={g.name} value={g.name}>{g.name}</MenuItem>
                                                                                        ))}
                                                                                    </Select>
                                                                                </FormControl>
                                                                            </Grid>
                                                                            <Grid item xs={12} sm="auto">
                                                                                <Stack direction="row" spacing={1}>
                                                                                    <Button
                                                                                        variant="contained"
                                                                                        size="small"
                                                                                        color="primary"
                                                                                        startIcon={<FaCheck />}
                                                                                        onClick={() => handleSaveMatchDetails(match._id)}
                                                                                    >
                                                                                        Speichern
                                                                                    </Button>
                                                                                    <Button
                                                                                        variant="outlined"
                                                                                        size="small"
                                                                                        startIcon={<FaTimes />}
                                                                                        onClick={() => setEditingMatchDetailsId(null)}
                                                                                    >
                                                                                        Abbrechen
                                                                                    </Button>
                                                                                </Stack>
                                                                            </Grid>
                                                                        </Grid>
                                                                    </Box>
                                                                </TableCell>
                                                            </TableRow>
                                                        )}
                                                    </React.Fragment>
                                                ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Box>
                    </Stack>
                )}

                {/* TAB 3: SPEISEN & GETränke */}
                {activeTab === 3 && (
                    <Stack spacing={4}>
                        {/* Quick action: load defaults */}
                        <Box display="flex" justifyContent="flex-end">
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={handleLoadDefaultCatering}
                                sx={{ borderRadius: '8px', textTransform: 'none' }}
                            >
                                ⬕ Standard-Getränke einfügen
                            </Button>
                        </Box>

                        <Paper variant="outlined" sx={{ p: 3, borderRadius: '12px' }}>
                            <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                                Speise- / Getränkeangebot hinzufügen
                            </Typography>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} sm={3}>
                                    <TextField 
                                        label="Name (z.B. Cola, Bier, Steakweck)" 
                                        fullWidth
                                        required 
                                        value={newItemName} 
                                        onChange={(e) => setNewItemName(e.target.value)} 
                                    />
                                </Grid>
                                <Grid item xs={12} sm={2.5}>
                                    <TextField 
                                        label="Menge/Einheit (z.B. Flasche (0,5 l))" 
                                        fullWidth
                                        value={newItemSize} 
                                        onChange={(e) => setNewItemSize(e.target.value)} 
                                    />
                                </Grid>
                                <Grid item xs={12} sm={2}>
                                    <TextField 
                                        label="Preis (z.B. 2,00 €)" 
                                        fullWidth
                                        value={newItemPrice} 
                                        onChange={(e) => setNewItemPrice(e.target.value)} 
                                    />
                                </Grid>
                                <Grid item xs={12} sm={2.5}>
                                    <FormControl fullWidth>
                                        <InputLabel>Kategorie</InputLabel>
                                        <Select
                                            value={newItemCategory}
                                            label="Kategorie"
                                            onChange={(e) => setNewItemCategory(e.target.value)}
                                        >
                                            <MenuItem value="Speisen">Speisen</MenuItem>
                                            <MenuItem value="Getränke">Getränke</MenuItem>
                                            <MenuItem value="Kaffee & Kuchen">Kaffee & Kuchen</MenuItem>
                                            <MenuItem value="Sonstiges">Sonstiges</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={2}>
                                    <TextField 
                                        label="Reihenfolge (Sortierung)" 
                                        fullWidth
                                        type="number"
                                        value={newItemOrder} 
                                        onChange={(e) => setNewItemOrder(e.target.value)} 
                                    />
                                </Grid>
                                <Grid item xs={12} sm={10}>
                                    <TextField 
                                        label="Zusatzinfo / Beschreibung (optional)" 
                                        fullWidth 
                                        size="small"
                                        value={newItemDescription} 
                                        onChange={(e) => setNewItemDescription(e.target.value)} 
                                        placeholder="z.B. mit Zwiebeln, alkoholfrei etc."
                                    />
                                </Grid>
                                <Grid item xs={12} sm={2}>
                                    <Button 
                                        variant="contained" 
                                        fullWidth
                                        startIcon={<FaPlus />} 
                                        onClick={handleAddCateringItem}
                                        sx={{ height: 40 }}
                                    >
                                        Hinzufügen
                                    </Button>
                                </Grid>
                            </Grid>
                        </Paper>

                        <Box>
                            <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                                Aktuelles Angebot ({catering?.length || 0} Artikel)
                            </Typography>
                            
                            {!catering || catering.length === 0 ? (
                                <Alert severity="info">Noch keine Speisen oder Getränke hinterlegt.</Alert>
                            ) : (
                                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '12px' }}>
                                    <Table size="small">
                                        <TableHead sx={{ bgcolor: '#fafafa' }}>
                                            <TableRow>
                                                <TableCell><strong>Sort.</strong></TableCell>
                                                <TableCell><strong>Kategorie</strong></TableCell>
                                                <TableCell><strong>Name</strong></TableCell>
                                                <TableCell><strong>Menge/Einheit</strong></TableCell>
                                                <TableCell><strong>Preis</strong></TableCell>
                                                <TableCell><strong>Beschreibung</strong></TableCell>
                                                <TableCell align="right"><strong>Aktion</strong></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {[...catering].sort((a,b) => (a.order || 0) - (b.order || 0)).map((item) => (
                                                <TableRow key={item._id} hover>
                                                    <TableCell sx={{ width: 80 }}>
                                                        {editingOrderId === item._id ? (
                                                            <TextField
                                                                size="small"
                                                                type="number"
                                                                value={editingOrderValue}
                                                                onChange={(e) => setEditingOrderValue(e.target.value)}
                                                                onBlur={() => handleSaveOrder(item._id)}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter') handleSaveOrder(item._id);
                                                                    if (e.key === 'Escape') { setEditingOrderId(null); }
                                                                }}
                                                                autoFocus
                                                                sx={{ width: 60 }}
                                                            />
                                                        ) : (
                                                            <Typography
                                                                variant="body2"
                                                                onClick={() => handleStartEditOrder(item)}
                                                                sx={{ cursor: 'pointer', textDecoration: 'underline dotted', display: 'inline-block' }}
                                                                title="Klicken zum Sortieren"
                                                            >
                                                                {item.order || 0}
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell><Chip label={item.category || 'Speisen'} size="small" variant="outlined" /></TableCell>
                                                    <TableCell><strong>
                                                        {editingNameId === item._id ? (
                                                            <TextField
                                                                size="small"
                                                                value={editingNameValue}
                                                                onChange={(e) => setEditingNameValue(e.target.value)}
                                                                onBlur={() => handleSaveName(item._id)}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter') handleSaveName(item._id);
                                                                    if (e.key === 'Escape') { setEditingNameId(null); }
                                                                }}
                                                                autoFocus
                                                                sx={{ width: 180 }}
                                                            />
                                                        ) : (
                                                            <Typography
                                                                variant="body2"
                                                                fontWeight="bold"
                                                                onClick={() => handleStartEditName(item)}
                                                                sx={{ cursor: 'pointer', textDecoration: 'underline dotted', display: 'inline-block' }}
                                                                title="Name bearbeiten"
                                                            >
                                                                {item.name}
                                                            </Typography>
                                                        )}
                                                    </strong></TableCell>
                                                    <TableCell sx={{ minWidth: 140 }}>
                                                        {editingSizeId === item._id ? (
                                                            <TextField
                                                                size="small"
                                                                value={editingSizeValue}
                                                                onChange={(e) => setEditingSizeValue(e.target.value)}
                                                                onBlur={() => handleSaveSize(item._id)}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter') handleSaveSize(item._id);
                                                                    if (e.key === 'Escape') { setEditingSizeId(null); }
                                                                }}
                                                                autoFocus
                                                                placeholder="z.B. Glas"
                                                                sx={{ width: 130 }}
                                                            />
                                                        ) : (
                                                            <Chip
                                                                label={item.size || 'Einheit setzen'}
                                                                size="small"
                                                                variant={item.size ? 'outlined' : 'dashed'}
                                                                onClick={() => handleStartEditSize(item)}
                                                                sx={{ cursor: 'pointer' }}
                                                                title="Klicken zum Bearbeiten"
                                                            />
                                                        )}
                                                    </TableCell>
                                                    <TableCell sx={{ minWidth: 120 }}>
                                                        {editingPriceId === item._id ? (
                                                            <TextField
                                                                size="small"
                                                                value={editingPriceValue}
                                                                onChange={(e) => setEditingPriceValue(e.target.value)}
                                                                onBlur={() => handleSavePrice(item._id)}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter') handleSavePrice(item._id);
                                                                    if (e.key === 'Escape') { setEditingPriceId(null); }
                                                                }}
                                                                autoFocus
                                                                placeholder="z.B. 2,50 €"
                                                                sx={{ width: 110 }}
                                                            />
                                                        ) : (
                                                            <Chip
                                                                label={item.price || 'Preis setzen'}
                                                                size="small"
                                                                variant={item.price ? 'filled' : 'outlined'}
                                                                color={item.price ? 'default' : 'warning'}
                                                                onClick={() => handleStartEditPrice(item)}
                                                                sx={{ cursor: 'pointer', fontWeight: 'bold' }}
                                                                title="Klicken zum Bearbeiten"
                                                            />
                                                        )}
                                                    </TableCell>
                                                    <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>{item.description || '–'}</TableCell>
                                                    <TableCell align="right">
                                                        <IconButton size="small" color="error" onClick={() => handleDeleteCateringItem(item._id)} title="Löschen">
                                                            <FaTrash size={14} />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Box>
                    </Stack>
                )}

                {/* TAB 4: SPONSOREN */}
                {activeTab === 4 && (
                    <Stack spacing={4}>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: '12px' }}>
                            <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                                Neuen Sponsor hinzufügen
                            </Typography>
                            <Box component="form" onSubmit={handleAddSponsor} display="flex" flexDirection="column" gap={2} maxWidth={400}>
                                <TextField 
                                    label="Name des Sponsors" 
                                    required 
                                    value={newSponsorName} 
                                    onChange={(e) => setNewSponsorName(e.target.value)} 
                                />
                                <TextField 
                                    label="Website Link" 
                                    value={newSponsorWebsite} 
                                    onChange={(e) => setNewSponsorWebsite(e.target.value)} 
                                    placeholder="https://example.com"
                                />
                                <Box>
                                    <Typography variant="caption" color="textSecondary" display="block" mb={0.5}>
                                        Sponsoren-Logo
                                    </Typography>
                                    <input 
                                        type="file" 
                                        accept="image/*"
                                        onChange={(e) => setNewSponsorLogo(e.target.files[0])}
                                    />
                                </Box>
                                <Button 
                                    type="submit" 
                                    variant="contained" 
                                    startIcon={<FaPlus />} 
                                    sx={{ alignSelf: 'flex-start' }}
                                >
                                    Sponsor speichern
                                </Button>
                            </Box>
                        </Paper>

                        <Box>
                            <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                                Eingetragene Sponsoren ({turnier.sponsors?.length || 0})
                            </Typography>
                            
                            {!turnier.sponsors || turnier.sponsors.length === 0 ? (
                                <Alert severity="info">Keine Sponsoren hinterlegt.</Alert>
                            ) : (
                                <Grid container spacing={2}>
                                    {turnier.sponsors.map((sponsor) => (
                                        <Grid item xs={6} sm={4} md={3} key={sponsor._id}>
                                            <Card variant="outlined" sx={{ p: 2, textAlign: 'center', height: 160, display: 'flex', flexDirection: 'column', justifyItems: 'center', alignItems: 'center', justifyContent: 'center' }}>
                                                {sponsor.logoUrl ? (
                                                    <Box 
                                                        component="img" 
                                                        src={`${API_BASE_URL}${sponsor.logoUrl}`} 
                                                        alt={sponsor.name}
                                                        sx={{ maxHeight: 60, maxWidth: '100%', objectFit: 'contain', mb: 1 }}
                                                    />
                                                ) : (
                                                    <Box height={60} display="flex" alignItems="center" mb={1}>
                                                        <FaHandshake size={30} style={{ color: '#ccc' }} />
                                                    </Box>
                                                )}
                                                <Typography variant="subtitle2" fontWeight="bold" noWrap sx={{ maxWidth: '100%' }}>
                                                    {sponsor.name}
                                                </Typography>
                                                <IconButton 
                                                    size="small" 
                                                    color="error" 
                                                    onClick={() => handleDeleteSponsor(sponsor._id)}
                                                    sx={{ mt: 1 }}
                                                >
                                                    <FaTrash size={12} />
                                                </IconButton>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            )}
                        </Box>
                    </Stack>
                )}

                {/* TAB 5: QR CODE / LIVE-LINK */}
                {activeTab === 5 && (
                    <Box textAlign="center" py={2}>
                        <Typography variant="h6" fontWeight="bold" mb={2}>
                            Öffentliche Grümpelturnier-Live-Seite teilen
                        </Typography>
                        
                        <Box 
                            component="img" 
                            src={`https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(shareUrl)}`} 
                            alt="QR-Code" 
                            sx={{ p: 2, bgcolor: 'white', border: '1px solid #ddd', borderRadius: '12px', mb: 3 }}
                        />

                        <Box bgcolor="#f9f9f9" p={2} borderRadius="8px" sx={{ wordBreak: 'break-all', maxWidth: '500px', mx: 'auto', mb: 3 }}>
                            <Typography variant="caption" color="textSecondary" display="block">Öffentlicher Live-Link:</Typography>
                            <Typography 
                                component="a" 
                                href={shareUrl} 
                                target="_blank" 
                                rel="noreferrer"
                                sx={{ color: 'primary.main', fontWeight: 'bold', textDecoration: 'none' }}
                            >
                                {shareUrl}
                            </Typography>
                        </Box>

                        <Button variant="contained" startIcon={<FaQrcode />} onClick={handlePrintQr}>
                            QR-Code drucken (für Plakate)
                        </Button>
                    </Box>
                )}
            </Box>

            <ConfirmModal 
                open={managerConfirmOpen}
                onClose={() => setManagerConfirmOpen(false)}
                onConfirm={handleConfirmManagerAction}
                title={managerConfirmTitle}
                message={managerConfirmMessage}
                confirmText="Löschen"
                type="delete"
            />
        </Paper>
    );
};

export default AdminGruempelTurnier;
